import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { CarrierBillingAccount } from "@redotech/redo-fulfillment-carrier-account-common/carrier-billing-accounts";
import { ICarrierAccount } from "@redotech/redo-fulfillment-rate-common/carrier-account";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { createContext, memo, ReactNode, useMemo } from "react";

export interface CarrierAccountsContextValue {
  merchantCarrierAccounts: ICarrierAccount[];
  redoChildCarrierAccounts: ICarrierAccount[];
  billingAccounts: CarrierBillingAccount[];
  getCarrierAccountName: (carrierAccountId: string) => string | undefined;
  getBillingAccount: (
    billingAccountId: string,
  ) => CarrierBillingAccount | undefined;
}

export const CarrierAccountsContext = createContext<
  CarrierAccountsContextValue | null | undefined
>(undefined);

export const CarrierAccountsProvider = memo(function CarrierAccountsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const carrierAccountsLoad = useLoad(
    async () => rpcClient.getCarrierAccounts(null),
    [rpcClient],
  );

  const billingAccountsLoad = useLoad(
    async () => rpcClient.getThirdPartyBillingAccounts(null),
    [rpcClient],
  );

  const value = useMemo<CarrierAccountsContextValue | null>(() => {
    if (!carrierAccountsLoad.value) {
      return null;
    }

    const { merchantCarrierAccounts, redoChildCarrierAccounts } =
      carrierAccountsLoad.value;

    // Build a map of carrier account ID to name
    const carrierAccountIdToName = new Map<string, string>();
    for (const account of [
      ...merchantCarrierAccounts,
      ...redoChildCarrierAccounts,
    ]) {
      // Use carrierAccountId for easypost accounts, _id for other accounts
      const accountId = account.carrierAccountId ?? account._id.toString();
      carrierAccountIdToName.set(accountId, account.name);
    }

    const billingAccounts = billingAccountsLoad.value?.accounts ?? [];
    const billingAccountsById = new Map<string, CarrierBillingAccount>();
    for (const account of billingAccounts) {
      billingAccountsById.set(account._id.toString(), account);
    }

    return {
      merchantCarrierAccounts,
      redoChildCarrierAccounts,
      billingAccounts,
      getCarrierAccountName: (carrierAccountId: string) =>
        carrierAccountIdToName.get(carrierAccountId),
      getBillingAccount: (billingAccountId: string) =>
        billingAccountsById.get(billingAccountId),
    };
  }, [carrierAccountsLoad.value, billingAccountsLoad.value]);

  return (
    <CarrierAccountsContext.Provider value={value}>
      {children}
    </CarrierAccountsContext.Provider>
  );
});
